import React, { useState, useEffect } from 'react';
import './all-categories.css';
import ModernHeader from '../../../components/layout/Headers/ModernHeader';
import ModernFootermenu from '../../../components/layout/Footer';
import { fetchFormattedCategoriesWithCount } from '../../../shared/actions/categoryAction';
import AllCategoriesLoadingScreen from '../../../components/loading/allCategories/AllCategoriesLoadingScreen';
import CategoryBox from '../../../features/cards/CategoryBox';
import { formatProfileTag } from '../../../shared/utils/function';

const AllCategories = () => {
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [categoriesPerPage, setCategoriesPerPage] = useState(9);

  // Fetch categories and process them once
  useEffect(() => {
    const loadCategories = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetchFormattedCategoriesWithCount();

        if (response.success && response.data) {
          setCategories(response.data);
        } else {
          setError(response.error || 'Failed to load categories');
        }
      } catch (err) {
        setError('An unexpected error occurred');
      } finally {
        setIsLoading(false);
      }
    };

    loadCategories();
  }, []);

  // Calculate most active categories from existing data
  const getMostActiveCategories = () => {
    if (categories.length === 0) return [];

    const maxCount = Math.max(...categories.map(c => c.count));
    return categories
      .sort((a, b) => b.count - a.count)
      .slice(0, 3)
      .map(category => ({
        ...category,
        percentage: Math.min((category.count / maxCount) * 100, 100)
      }));
  };

  const loadMoreCategories = () => {
    setCategoriesPerPage(prev => prev + 9);
  };

  const displayedCategories = categories.slice(0, categoriesPerPage);
  const hasMoreCategories = categories.length > categoriesPerPage;
  const mostActiveCategories = getMostActiveCategories();

  return (
    <div className="all-categories-page fade-in">
      <ModernHeader
        showBackButton={true}
        backButtonUrl="/discover"
        // hideSearchIcon={true}
        showAvatar={false}
      />

      <main className="all-categories-main">
        {/* All Categories Grid */}
        <section className="categories-grid-section">
          <div className="section-header">
            <h3 className="section-title">All Categories</h3>
          </div>
          {isLoading ?
            <AllCategoriesLoadingScreen />
            :
            <>
              <div className="all-categories-grid">
                {displayedCategories.length > 0 ? (
                  displayedCategories.map((category, index) => (
                    <CategoryBox
                      key={`${category.name}-${index}`}
                      category={category}
                      index={index}
                    />
                  ))
                ) : (
                  <div className="no-content-container">
                    No categories found
                  </div>
                )}
              </div>

              {hasMoreCategories && (
                <div className="button-load">
                  <button className="load-more-btn" onClick={loadMoreCategories}>
                    <i className="fa-solid fa-plus"></i>
                    Load More Categories
                  </button>
                </div>
              )}
            </>
          }
        </section>

        {/* Most Active Categories */}
        {mostActiveCategories.length > 0 && (
          <section className="most-active-section">
            <h3 className="section-title">Most Active Categories</h3>

            <div className="activity-card">
              <div className="activity-content">
                {mostActiveCategories.map((category, index) => (
                  <div
                    key={`${category.name}-${index}`}
                    className="activity-item"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="activity-info">
                      <div className="activity-indicator"></div>
                      <span className="activity-name">
                        {formatProfileTag(category.name)}
                      </span>
                    </div>
                    <div className="activity-stats">
                      <div className="progress-bar-container">
                        <div
                          className="progress-bar"
                          style={{ width: `${category.percentage}%` }}
                        ></div>
                      </div>
                      <span className="activity-count">
                        {category.creators}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <ModernFootermenu />
    </div>
  );
};

export default AllCategories; 